import {
  cloneDeep,
  filter,
  first,
  floor,
  get,
  map,
  orderBy,
  slice,
  sum,
  uniq,
} from 'lodash'
import { generateLabelsFromDates } from '@/screens/Liveboard/Liveboard.util'

import { DEFAULT_CHART_BG_COLOR } from '@/screens/Liveboard/Liveboard.constant'
import {
  resolveTitleColumnWidth,
  resolveValueColumnWidth,
  getTitleColumnBounds,
  getValueColumnBounds,
} from './Table.utils'

const MAX_BAR_Thickness = 60
function findItemByKey(data = [], valueKey) {
  for (const [key, value] of Object.entries(data)) {
    if (key == valueKey) {
      return value
    }
  }
}

export const transformMultiTwoColumnChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) => item.display_name)
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )
  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((item) => item[index + 1]),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`
      ),
      datalabels: {
        color: 'rgb(255, 255, 255)',
      },
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiLineTimeChart = (data, chartItem) => {
  let labels = null
  let valueToShow = null
  let labelDataSet = null

  if (!chartItem?.dynamic_group_name) {
    labels = get(data, 'rows', []).map((item) => first(item))
    valueToShow = get(data, 'cols', []).map((item) =>
      get(item, 'display_name', '')
    )
    labelDataSet = valueToShow.filter((element) =>
      get(chartItem, 'configure.chart_value', []).includes(element)
    )
  } else {
    labels = uniq(get(data, 'rows', []).map((item) => first(item)))
    valueToShow = get(data, 'cols', []).map((item) =>
      get(item, 'display_name', '')
    )

    const defectMap = {}
    data?.rows?.forEach((item) => {
      const date = item[0]
      const defect = item[1]
      const defectCount = item[2]

      if (!defectMap[defect]) {
        defectMap[defect] = Array(labels.length).fill(0)
      }

      const index = labels.indexOf(date)
      defectMap[defect][index] = defectCount
    })

    const datasets = Object.keys(defectMap).map((defect, index) => ({
      label: defect,
      hidden: false,
      fill: false,
      data: defectMap[defect],
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ), // Assign random colors for visualization
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ),
      datalabels: {
        display: true,
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          ''
        ),
      },
      tension: 0.5,
      spanGaps: true,
    }))

    return {
      labels,
      datasets,
    }
  }
}

export const transformMultiLineChart = (
  data,
  chartItem,
  datePicked?,
  currentPeriod?
) => {
  let labels = null
  let valueToShow = null
  let labelDataSet = null

  let groupData = []

  const generatedLabels = generateLabelsFromDates(datePicked, currentPeriod)

  if (!chartItem?.dynamic_group_name) {
    labels = get(data, 'rows', []).map((item) => first(item))
    valueToShow = get(data, 'cols', []).map((item) =>
      get(item, 'display_name', '')
    )
    labelDataSet = valueToShow.filter((element) =>
      get(chartItem, 'configure.chart_value', []).includes(element)
    )
  } else {
    labels =
      generatedLabels || uniq(get(data, 'rows', []).map((item) => first(item)))
    valueToShow = get(data, 'cols', []).map((item) =>
      get(item, 'display_name', '')
    )

    groupData = data?.rows
      ?.filter((curr) => curr[1] != null)
      ?.reduce((acc, curr) => {
        const key = curr[1]
        if (!acc[key]) acc[key] = Array(labels.length).fill(0)
        const idx = labels.indexOf(curr[0])
        if (idx !== -1) acc[key][idx] += curr[2] ?? 0
        return acc
      }, {})
    const tmpGroupData = []
    for (var defect in groupData) {
      tmpGroupData.push({
        name: defect,
        value: groupData[defect].reduce((a, b) => {
          a = a + b
          return a
        }, 0),
      })
    }

    labelDataSet = slice(orderBy(tmpGroupData, ['value'], ['desc']), 0, 5).map(
      (item) => item.name
    )
  }

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      hidden: false,
      fill: false,
      data: !chartItem?.dynamic_group_name
        ? get(data, 'rows', []).map((row) =>
            floor(row[index + 1], chartItem?.precision ?? 0)
          )
        : findItemByKey(groupData, item),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ),
      datalabels: {
        display: true,
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          ''
        ),
      },
      tension: 0.5,
      spanGaps: true,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiHundredPercentStackedColumnChart = (
  data,
  chartItem
) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((item) => item[index + 1]),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ),
      datalabels: {
        color: 'rgb(255, 255, 255)',
      },
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiColumnChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,

      data: get(data, 'rows', []).map((row) => {
        return floor(get(row, `[${index + 1}]`), chartItem?.precision ?? 0)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      datalabels: {
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          DEFAULT_CHART_BG_COLOR
        ),
      },
      borderRadius: 10,
      maxBarThickness: MAX_BAR_Thickness,
    }
  })

  return {
    labels,
    datasets,
  }
}

export const transformMultiColumnAndLineChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      type: index === 1 ? 'bar' : 'line',
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return floor(get(row, `[${index + 1}]`), chartItem?.precision)
      }),

      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      datalabels: {
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          DEFAULT_CHART_BG_COLOR
        ),
      },
      borderRadius: 10,
      tension: 0.5,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiBarChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      type: 'bar',
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return floor(get(row, `[${index + 1}]`), chartItem?.precision ?? 0)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      datalabels: {
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          DEFAULT_CHART_BG_COLOR
        ),
      },
      borderRadius: 10,
      maxBarThickness: MAX_BAR_Thickness,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiGaugeChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = filter(
    get(data, 'cols', []).map((item) => get(item, 'display_name', '')),
    (val) => val !== '_id'
  )

  return {
    labels: valueToShow,
    datasets: [
      {
        label: '',
        data: map(valueToShow, (value, index) =>
          sum(
            get(data, 'rows', []).map(
              (item) => floor(item[index + 1]),
              chartItem?.precision ?? 0
            )
          )
        ),
        backgroundColor: get(
          chartItem,
          ['configure', 'options', 'backgroundColor'],
          [DEFAULT_CHART_BG_COLOR]
        ),
        datalabels: {
          color: map(valueToShow, (value) => '#fff'),
        },
      },
    ],
  }
}

export const transformMultiAreaChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      tension: 0.5,
      fill: true,
      backgroundColor: get(
        chartItem,
        `configure.options.bgColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiHundredPercentStakeBarChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      tension: 0.5,
      fill: true,
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(131, 131, 131, 1)'
      ),
      datalabels: {
        color: 'rgba(255, 255, 255, 1)',
      },
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}
export const transformMultiDoughnutChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return floor(get(row, `[${index + 1}]`), chartItem?.precision ?? 0)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiPieChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])

  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return floor(get(row, `[${index + 1}]`), chartItem?.precision ?? 0)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor`,
        'rgba(132, 132, 132, 0.3)'
      ),
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiTwoBarChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return floor(get(row, `[${index + 1}]`), chartItem?.precision ?? 0)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiMultiChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet?.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return floor(get(row, `[${index + 1}]`), chartItem?.precision ?? 0)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      fill: true,
      tension: 0.5,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiGaugeTargetChart = (data, value = 0) => {
  const _original = {
    ...cloneDeep(data),
    labels: ['', '', ''],
    datasets: [
      {
        label: '',
        data: [0, 0, 0],
        backgroundColor: [
          'rgba(0, 0, 0, 0)',
          'rgba(173, 173, 173, 1)',
          'rgba(0, 0, 0, 0)',
        ],
      },
    ],
  }

  if (!value) return _original
  if (isNaN(value)) return _original

  let _data = []
  if (value > 100 || value <= 0 || !value || isNaN(value)) _data = [0, 0, 0]
  else _data = [value - 1, 1, 100 - value]

  return {
    ...cloneDeep(data),
    labels: ['', '', ''],
    datasets: ['', '', '', '', '', '', '', '', ''].map((item, i) => {
      if (i % 2 === 1)
        return {
          weight: 1,
        }
      return {
        label: '',
        data: _data,
        backgroundColor: [
          'rgba(0, 0, 0, 0)',
          'rgba(173, 173, 173, 1)',
          'rgba(0, 0, 0, 0)',
        ],
      }
    }),
  }
}

const formatTableCell = (cell, precision) => {
  if (typeof cell !== 'number' || Number.isNaN(cell)) {
    return cell == null || Number.isNaN(cell) ? '' : cell
  }
  return floor(cell, precision)
}

const isTotalColumn = (title: string, index: number, total: number) => {
  return total > 1 && index === total - 1 && /^total$/i.test(String(title).trim())
}

export const transformTableChart = (data, item) => {
  const cols = get(data, 'cols', [])
  const rows = get(data, 'rows', [])
  const precision = item?.precision
  const configuredNames = get(item, 'configure.chart_value', [])

  const selectedIndexes = configuredNames.length
    ? configuredNames.map((name) =>
        cols.findIndex((col) => col?.name === name)
      )
    : cols.map((_, index) => index)

  const selectedCols = selectedIndexes.map((index) =>
    index === -1 ? null : cols[index]
  )
  const selectedRows = rows.map((value) =>
    selectedIndexes.map((index) =>
      index === -1 ? '' : formatTableCell(get(value, index), precision)
    )
  )

  const columns = selectedCols.map((col, index) => {
    const dataIndex = `col_${index}`
    if (!col) {
      return {
        title: '',
        dataIndex,
        key: dataIndex,
      }
    }

    const title = col.display_name || col.name || ''
    const isFirst = index === 0
    const isTotal = isTotalColumn(title, index, selectedCols.length)
    const { minWidth, maxWidth } = isFirst
      ? getTitleColumnBounds()
      : getValueColumnBounds()
    const columnWidth = isFirst
      ? resolveTitleColumnWidth(
          title,
          selectedRows.map((row) => row[index])
        )
      : resolveValueColumnWidth(
          title,
          selectedRows.map((row) => row[index])
        )
    const sizeStyle = {
      width: columnWidth,
      minWidth,
      maxWidth,
    }

    return {
      title: (
        <span className="table-chart-col-title" title={title}>
          {title}
        </span>
      ),
      dataIndex,
      key: dataIndex,
      align: isFirst ? 'left' : 'right',
      fixed: isFirst ? 'left' : isTotal ? 'right' : undefined,
      className: [
        isFirst ? 'table-chart-title-col' : undefined,
        isTotal ? 'table-chart-total-col' : undefined,
      ]
        .filter(Boolean)
        .join(' ') || undefined,
      width: columnWidth,
      onHeaderCell: () => ({ style: sizeStyle }),
      onCell: () => ({ style: sizeStyle }),
    }
  })
  const row = selectedRows.map((value, rowIndex) => {
    const record = { key: rowIndex }
    value.forEach((cell, index) => {
      record[`col_${index}`] = cell
    })
    return record
  })

  return {
    row,
    chart_value: columns,
  }
}
